import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { Container } from "reactstrap";
import styled from "styled-components";

import { BalanceChip } from "../../components/Credits";
import NewFooter from "../../components/Footers/NewFooter";
import NewNavbar from "../../components/Navbar/NewNavbar";
import { API_URL } from "../../constants";
import { FREE_MONTHLY_CREDITS, formatCreditsNumber } from "../../constants/credits";
import { useAccount } from "../../contexts/AccountContext";
import { track } from "../../utils/analytics";
import { authHeader, clearToken, getToken } from "../../utils/auth";
import { storageGet, storageSet } from "../../utils/storage";
import {
  REFUND_REASSURANCE,
  formatDate,
  groupByProperty,
  hasUnsharedEdits,
  isPollingStatus,
  primaryActionFor,
  propertyStatusMeta,
  readyFilesLine,
  seedsListing,
} from "./propertyFeed";

/** @typedef {import("./propertyFeed").PropertyGroup} PropertyGroup */
/** @typedef {import("./propertyFeed").ReportSummary} ReportSummary */
/** @typedef {{ logo_url?: string | null, headshot_url?: string | null }} AccountBranding */
/** @typedef {{ branding?: AccountBranding }} AccountSummary */

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: linear-gradient(180deg, #eef5f4 0%, #f9fafb 340px);
`;

const ContentWrapper = styled.div`
  flex: 1;
  padding: 120px 0 70px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 24px;
`;

const TitleBlock = styled.div`
  min-width: 0;
`;

const Eyebrow = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: #0e7e7a;
  margin-bottom: 6px;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
  letter-spacing: -0.02em;
  display: flex;
  align-items: baseline;
  gap: 10px;

  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const Subtitle = styled.p`
  font-size: 0.95rem;
  color: #6b7280;
  margin: 6px 0 0;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;
`;

const AccountLink = styled(Link)`
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
  text-decoration: none;
  min-height: 44px;
  display: inline-flex;
  align-items: center;

  &:hover {
    color: #0e7e7a;
    text-decoration: none;
  }
`;

const ReportsTotal = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: #6b7280;
`;

const ReportCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 18px 20px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const HeroThumb = styled.img`
  width: 64px;
  height: 64px;
  border-radius: 10px;
  object-fit: cover;
  flex-shrink: 0;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
`;

const HeroThumbPlaceholder = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 10px;
  flex-shrink: 0;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.6rem;
`;

const ReportAddress = styled.div`
  font-size: 1.05rem;
  font-weight: 700;
  color: #1a1a1a;
`;

const ReportMeta = styled.div`
  font-size: 0.85rem;
  color: #6b7280;
  margin-top: 2px;
`;

const UnsharedNote = styled.span`
  color: #92400e;
  font-weight: 600;
`;

// One calm sentence under a failed row: the charge came back. A fact, not a
// warning colour.
const ReassuranceNote = styled.div`
  font-size: 0.85rem;
  color: #6b7280;
  margin-top: 4px;
`;

const ReportActions = styled.div`
  display: flex;
  gap: 10px;
  flex-shrink: 0;
`;

const ActionLink = styled.a`
  font-size: 0.9rem;
  font-weight: 600;
  border-radius: 999px;
  padding: 8px 18px;
  text-decoration: none;
  cursor: pointer;
  border: 1px solid #d1d5db;
  color: #1a1a1a;
  background: #ffffff;

  &:hover {
    background: #f3f4f6;
  }
`;

const PrepareButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.92rem;
  font-weight: 700;
  border-radius: 999px;
  padding: 10px 20px;
  min-height: 44px;
  box-sizing: border-box;
  text-decoration: none;
  cursor: pointer;
  border: 1px solid #0e7e7a;
  background: #0e7e7a;
  color: #ffffff;

  &:hover {
    background: #0b605d;
    color: #ffffff;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  max-width: 360px;
  font-size: 0.95rem;
  color: #1a1a1a;
  border: 1px solid #d1d5db;
  border-radius: 999px;
  padding: 10px 16px;
  margin: 0 0 16px;

  &:focus {
    outline: none;
    border-color: #0e7e7a;
  }
`;

const StatusBadge = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 4px 12px;
  border-radius: 999px;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$fg};
`;

const PropertyStatus = styled(StatusBadge)`
  margin-top: 6px;
  display: inline-block;
`;

// The card body doubles as a tap target for the card's primary destination —
// on a phone the whole hero+address area opens the prep, not just the button.
const CardBodyLink = styled.a`
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
  text-decoration: none;
  color: inherit;

  &:hover {
    color: inherit;
    text-decoration: none;
  }
`;

// The ONE primary action on a property card, emphasis by state.
const PrimaryAction = styled.a`
  font-size: 0.9rem;
  font-weight: 700;
  border-radius: 999px;
  padding: 10px 20px;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  text-decoration: none;
  cursor: pointer;
  border: 1px solid #0e7e7a;
  background: #0e7e7a;
  color: #ffffff;

  &:hover {
    background: #0b605d;
    color: #ffffff;
  }
`;

const PreparingChip = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: #92400e;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
`;

// --- Profile-completion banner (links to the Account page) ---
const Banner = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 14px;
  padding: 14px 18px;
  margin-bottom: 20px;
`;

const BannerText = styled.button`
  flex: 1;
  text-align: left;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 0.92rem;
  color: #065f46;

  strong {
    font-weight: 700;
  }
`;

const BannerDismiss = styled.button`
  flex-shrink: 0;
  background: none;
  border: none;
  font-size: 1.3rem;
  line-height: 1;
  color: #059669;
  cursor: pointer;
  padding: 0 4px;

  &:hover {
    color: #065f46;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 50px 0;
  color: #6b7280;
  background: #ffffff;
  border: 1px dashed #e5e7eb;
  border-radius: 14px;
`;

const Centered = styled.div`
  text-align: center;
  padding: 80px 0;
  color: #4a4a4a;

  h2 {
    font-size: 1.4rem;
    color: #1a1a1a;
    margin-bottom: 8px;
  }
`;

const Spinner = styled.div`
  width: 36px;
  height: 36px;
  border: 3px solid #e5e7eb;
  border-top-color: #6b7280;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 16px;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

/** A single property card: hero, address, status pill, and ONE primary action
 * for that state. Tapping the card body opens the property's SPACE (its prep,
 * assets, version history, and property-level actions) — the card itself stays
 * a one-decision surface.
 * @param {{ property: PropertyGroup }} props
 */
const PropertyCard = ({ property }) => {
  const { latest } = property;
  const meta =
    latest.status === "preparing" || latest.video_only ? propertyStatusMeta(latest.status) : null;
  const spaceUrl =
    latest.video_only && latest.listing_uuid
      ? `/create-video/${latest.listing_uuid}`
      : `/properties/${latest.listing_uuid || latest.computation_uuid}`;
  const updatedIso = latest.edited_at || latest.shared_at || latest.created_at;
  const unshared = hasUnsharedEdits(latest);

  const action = primaryActionFor(latest);
  const { href: primaryHref, label: primaryLabel, external } = action;
  const externalProps = external ? { target: "_blank", rel: "noopener noreferrer" } : {};
  const trackOpen = () => {
    // "Finish preparing" / "Try again" return to the
    // confirmation page, which reads the listing from session storage — seed
    // it or the page loads empty.
    if (seedsListing(action) && latest.listing_uuid) {
      storageSet("currentListingId", latest.listing_uuid);
    }
    track("prep_property_opened", { status: latest.status || "ready" });
  };
  const primary =
    latest.status === "preparing" ? (
      <PreparingChip>Preparing your listing</PreparingChip>
    ) : primaryHref ? (
      <PrimaryAction href={primaryHref} {...externalProps} onClick={trackOpen}>
        {primaryLabel}
      </PrimaryAction>
    ) : (
      // Legacy edge: a prep with no editable draft and no file yet — never
      // leave the card action-less.
      <ActionLink href="mailto:help@listino.ai">Contact support</ActionLink>
    );

  // A prep that has assets to send — surface the package on the card (the
  // multi-asset payoff should read at entry, not only at the Share step).
  // Under credits only the files actually rendered are named.
  const filesLine =
    !!latest.editable && ["ready", "personalized", "shared"].includes(latest.status || "")
      ? readyFilesLine(latest)
      : "";
  const reassurance = latest.status === "needs_attention";

  return (
    <ReportCard>
      <CardBodyLink
        href={spaceUrl}
        onClick={trackOpen}
        aria-label={`Open property: ${latest.address || "your listing"}`}
      >
        {latest.hero_image_url ? (
          <HeroThumb src={latest.hero_image_url} alt="" />
        ) : (
          <HeroThumbPlaceholder aria-hidden="true">🏡</HeroThumbPlaceholder>
        )}
        <div>
          <ReportAddress>{latest.address || "Your listing"}</ReportAddress>
          {meta && (
            <PropertyStatus $bg={meta.$bg} $fg={meta.$fg}>
              {meta.label}
            </PropertyStatus>
          )}
          <ReportMeta>
            Updated {formatDate(updatedIso)}
            {unshared && <UnsharedNote> · unshared edits</UnsharedNote>}
            {filesLine && ` · ${filesLine}`}
          </ReportMeta>
          {reassurance && <ReassuranceNote>{REFUND_REASSURANCE}</ReassuranceNote>}
        </div>
      </CardBodyLink>
      <ReportActions>
        {primary}
        {!latest.video_only && latest.video_url && latest.listing_uuid && (
          <ActionLink href={`/create-video/${latest.listing_uuid}`}>Manage video</ActionLink>
        )}
      </ReportActions>
    </ReportCard>
  );
};

/** The dashboard is "Your listings": one card per property, the prep and its
 * assets hanging underneath. Account/branding/prep defaults live at /account. */
const DashboardPage = () => {
  const history = useHistory();
  // The account (and, under credits, the balance) comes from the shared
  // context: one /auth/me/ for the whole app, refreshed after every spend.
  const { account, credits, error: accountError } = useAccount();
  const [reports, setReports] = useState(/** @type {ReportSummary[]} */ ([]));
  const [query, setQuery] = useState("");
  const [feedStatus, setFeedStatus] = useState(
    /** @type {"loading" | "ok" | "error"} */ ("loading")
  );
  const [bannerDismissed, setBannerDismissed] = useState(
    () => storageGet("liq_profile_banner_dismissed", "local") === "1"
  );

  const dismissBanner = useCallback(() => {
    storageSet("liq_profile_banner_dismissed", "1", "local");
    setBannerDismissed(true);
  }, []);

  useEffect(() => {
    if (!getToken()) {
      history.push("/login");
      return;
    }
    axios
      .get(`${API_URL}/auth/me/reports/`, { headers: authHeader() })
      .then((reportsResp) => {
        setReports(reportsResp.data.reports || []);
        setFeedStatus("ok");
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          clearToken();
          history.push("/login");
          return;
        }
        console.error("Dashboard load failed:", err);
        setFeedStatus("error");
      });
  }, [history]);

  // The page is ready when the feed is in AND the shared account has arrived;
  // a failed account fetch is an error, a pending one is still loading.
  const status =
    feedStatus === "error" || accountError
      ? "error"
      : feedStatus === "loading" || !account
        ? "loading"
        : "ok";

  const shouldPoll =
    feedStatus === "ok" && reports.some((report) => isPollingStatus(report.status));

  // Poll only while server-truthful work states can still transition. Depending
  // on the boolean keeps an unchanged in-flight feed from resetting the 8s clock.
  useEffect(() => {
    if (!shouldPoll) return undefined;
    const handle = window.setInterval(() => {
      axios
        .get(`${API_URL}/auth/me/reports/`, { headers: authHeader() })
        .then((resp) => setReports(resp.data.reports || []))
        .catch((err) => {
          if (err.response?.status === 401) {
            clearToken();
            history.push("/login");
          }
          // Other transient failures retain the current feed until the next tick.
        });
    }, 8000);
    return () => window.clearInterval(handle);
  }, [history, shouldPoll]);

  // Group the flat feed into property cards, then apply the search filter.
  const properties = groupByProperty(reports);
  const trimmedQuery = query.trim().toLowerCase();
  const visibleProperties = trimmedQuery
    ? properties.filter((p) => (p.latest.address || "").toLowerCase().includes(trimmedQuery))
    : properties;

  let body;
  if (status === "loading") {
    body = (
      <Centered>
        <Spinner />
        <p>Loading your listings…</p>
      </Centered>
    );
  } else if (status === "error" || !account) {
    body = (
      <Centered>
        <h2>Something went wrong</h2>
        <p>
          We couldn't load your listings. Try refreshing, or email{" "}
          <a href="mailto:help@listino.ai">help@listino.ai</a>.
        </p>
      </Centered>
    );
  } else {
    const branding = /** @type {AccountSummary} */ (account).branding || {};
    const profileIncomplete = !branding.logo_url || !branding.headshot_url;
    // Credits: the Free plan's empty state states what the month includes —
    // a fact about the plan, not a pitch. Paid plans keep the plan-neutral line.
    const freePlanEmpty = credits?.plan_key === "free";
    body = (
      <>
        {profileIncomplete && !bannerDismissed && (
          <Banner>
            <BannerText type="button" onClick={() => history.push("/account")}>
              <strong>Finish your broker profile.</strong> Add your{" "}
              {!branding.logo_url && !branding.headshot_url
                ? "brokerage logo and photo"
                : !branding.logo_url
                  ? "brokerage logo"
                  : "broker photo"}{" "}
              so every listing package is fully branded.
            </BannerText>
            <BannerDismiss type="button" aria-label="Dismiss" onClick={dismissBanner}>
              ×
            </BannerDismiss>
          </Banner>
        )}
        {properties.length > 3 && (
          <SearchInput
            type="search"
            placeholder="Search your listings…"
            aria-label="Search your listings"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        )}
        {properties.length === 0 ? (
          <EmptyState>
            {freePlanEmpty ? (
              <>
                Your Free plan includes {formatCreditsNumber(FREE_MONTHLY_CREDITS)} credits every
                month — enough to prepare your first listing analysis on us. Hit{" "}
                <strong>Prepare a listing</strong> to start.
              </>
            ) : (
              <>
                Your listings will show here. Hit <strong>Prepare a listing</strong> to get
                client-ready in minutes.
              </>
            )}
          </EmptyState>
        ) : visibleProperties.length === 0 ? (
          <EmptyState>No listings match “{query}”.</EmptyState>
        ) : (
          visibleProperties.map((property) => (
            <PropertyCard key={property.key} property={property} />
          ))
        )}
      </>
    );
  }

  return (
    <PageContainer>
      <NewNavbar />
      <ContentWrapper>
        <Container style={{ maxWidth: 940 }}>
          <HeaderRow>
            <TitleBlock>
              <Eyebrow>Listino</Eyebrow>
              <Title>
                Your listings
                {status === "ok" && properties.length > 0 && (
                  <ReportsTotal>
                    {properties.length} listing{properties.length === 1 ? "" : "s"}
                  </ReportsTotal>
                )}
              </Title>
              <Subtitle>
                Every property you're working on, with its listing package underneath.
              </Subtitle>
            </TitleBlock>
            <HeaderActions>
              <AccountLink to="/account">Account &amp; settings</AccountLink>
              <BalanceChip />
              <PrepareButton href="/verify" onClick={() => track("prep_prepare_clicked")}>
                Prepare a listing
              </PrepareButton>
            </HeaderActions>
          </HeaderRow>
          {body}
        </Container>
      </ContentWrapper>
      <NewFooter />
    </PageContainer>
  );
};

export default DashboardPage;
